import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GetFridgeSchemasTemplates,
  ListScheduledFridgeSchemaChanges,
  CancelScheduledFridgeSchemaChange,
} from '../../libs/GraphQL';
import { filterActiveSchemas, isBaseSchema } from '../../utils/schemaLifecycle';
import { getActiveSchemaIdSet } from '../../utils/activeSchemaIds';
import { formatScheduleDateTime } from '../SchemaTemplates/SchemaTemplatesUtils';
import Badge from '../../components/atoms/Badge';
import ScheduledChangeModal from './ScheduledChangeModal';
import ClockIcon from '../../assets/ClockIcon';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import buildTsv from '../../utils/buildTsv';
import copyToClipboard from '../../utils/copyToClipboard';
import TipsPanel from '../../components/atoms/TipsPanel';
import styles from './SchemaConditionsLanding.module.css';

const SCHEMA_CONDITIONS_TIPS = [
  {
    title: 'Filter by a list of fridges',
    body: (
      <>
        Type a Schema ID into the search box, or paste a list (comma, newline,
        or mixed) into <strong>Or paste schema IDs</strong> to target a specific
        group. The counter shows how many IDs matched.
      </>
    ),
  },
  {
    title: 'Find fridges with pending scheduled changes',
    body: (
      <>
        Click the <strong>Scheduled</strong> filter pill to see only fridges
        with a queued change. Click any purple clock to view the details or
        cancel before it fires.
      </>
    ),
  },
  {
    title: 'Round-trip edit',
    body: (
      <>
        Filter to what you want, click <strong>Copy to Clipboard</strong>, paste
        into Sheets or Excel, edit the conditions, then paste those rows into
        the CSV on <strong>Manage Fridge Schema Conditions</strong>. Column
        order matches — no reformatting needed.
      </>
    ),
  },
];

const emptyFilters = () => ({
  types: new Set(),
  platforms: new Set(),
  priceTypes: new Set(),
  base: false,
  nfe: false,
  scheduled: false,
});

const SchemaConditionsLanding = () => {
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schemaConditions, setSchemaConditions] = useState([]);
  const [showConditions, setShowConditions] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [allSchemaData, setAllSchemaData] = useState([]); // Cache full schema data
  const [filterListInput, setFilterListInput] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  // schemaId -> its pending scheduled rows (full rows, so the modal has
  // jobId + payload).
  const [pendingBySchema, setPendingBySchema] = useState({});
  const [modalSchemaId, setModalSchemaId] = useState(null);
  const [cancellingKey, setCancellingKey] = useState(null);
  const [cancelError, setCancelError] = useState(null);
  const [pendingError, setPendingError] = useState(null);

  const debouncedFilterListInput = useDebouncedValue(filterListInput, 250);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 200);

  const parsedFilterIds = useMemo(() => {
    const ids = debouncedFilterListInput
      .split(/[\s,]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    return new Set(ids);
  }, [debouncedFilterListInput]);

  const filterCounts = useMemo(() => {
    if (parsedFilterIds.size === 0) return null;
    const knownSet = new Set(allSchemaData.map((s) => s.schemaId));
    let matched = 0;
    let notFound = 0;
    parsedFilterIds.forEach((id) => {
      if (knownSet.has(id)) matched += 1;
      else notFound += 1;
    });
    return { matched, notFound };
  }, [parsedFilterIds, allSchemaData]);

  // Filter pills are built from the viewed set so the options stay stable while
  // you narrow; the table renders the filtered subset (displayedConditions).
  const facets = useMemo(() => {
    const types = new Set();
    const platforms = new Set();
    const priceTypes = new Set();
    let hasBase = false;
    let hasNfe = false;
    let hasScheduled = false;
    schemaConditions.forEach((c) => {
      if (c.schemaType) types.add(c.schemaType);
      if (c.platform) platforms.add(c.platform);
      if (c.priceDisplayType) priceTypes.add(c.priceDisplayType);
      if (isBaseSchema(c.schemaId)) hasBase = true;
      if (c.newFridgeExperienceEnabled) hasNfe = true;
      if ((pendingBySchema[c.schemaId] || []).length) hasScheduled = true;
    });
    return {
      types: [...types].sort(),
      platforms: [...platforms].sort(),
      priceTypes: [...priceTypes].sort(),
      hasBase,
      hasNfe,
      hasScheduled,
    };
  }, [schemaConditions, pendingBySchema]);

  const displayedConditions = useMemo(
    () =>
      schemaConditions.filter((c) => {
        if (filters.base && !isBaseSchema(c.schemaId)) return false;
        if (filters.nfe && !c.newFridgeExperienceEnabled) return false;
        if (filters.scheduled && !(pendingBySchema[c.schemaId] || []).length)
          return false;
        if (filters.types.size > 0 && !filters.types.has(c.schemaType)) {
          return false;
        }
        if (filters.platforms.size > 0 && !filters.platforms.has(c.platform)) {
          return false;
        }
        if (
          filters.priceTypes.size > 0 &&
          !filters.priceTypes.has(c.priceDisplayType)
        ) {
          return false;
        }
        return true;
      }),
    [schemaConditions, filters, pendingBySchema],
  );

  const anyFilterActive =
    filters.types.size > 0 ||
    filters.platforms.size > 0 ||
    filters.priceTypes.size > 0 ||
    filters.base ||
    filters.nfe ||
    filters.scheduled;

  const toggleSetFilter = (key, value) => {
    setFilters((prev) => {
      const next = new Set(prev[key]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, [key]: next };
    });
  };

  const toggleBoolFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearFilters = () => setFilters(emptyFilters());

  const noResultsMessage = () =>
    parsedFilterIds.size > 0
      ? 'No schemas matched the pasted IDs.'
      : 'No schema conditions found for the selected schema.';

  const getFilteredConditions = () => {
    if (parsedFilterIds.size > 0) {
      return allSchemaData.filter((c) => parsedFilterIds.has(c.schemaId));
    }
    if (selectedSchema !== 'ALL') {
      return allSchemaData.filter((c) => c.schemaId === selectedSchema);
    }
    return allSchemaData;
  };

  const fetchSchemas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // null on failure so a fleet hiccup degrades to unfiltered schemas
      // rather than failing the page; the schema fetch itself still throws.
      const [result, activeSchemaIds] = await Promise.all([
        GetFridgeSchemasTemplates({}),
        getActiveSchemaIdSet().catch(() => null),
      ]);
      // Hide schemas for offline/decommissioned fridges; keep base templates.
      // Sort by schemaId so the table/downloads come back alphabetical
      // instead of in scan order.
      const filtered = activeSchemaIds
        ? filterActiveSchemas(result || [], activeSchemaIds, {
            includeBaseSchemas: true,
          })
        : result || [];
      const fullData = filtered.sort((a, b) =>
        (a.schemaId || '').localeCompare(b.schemaId || ''),
      );
      setAllSchemaData(fullData);
      const uniqueSchemas = [
        ...new Set(
          fullData
            .filter((schema) => schema.pricingType !== 'BASE_PMT')
            .map((schema) => schema.schemaId),
        ),
      ].sort();
      setSchemas(uniqueSchemas);
    } catch (err) {
      console.error('Error fetching schemas:', err);
      setError('Failed to fetch schema IDs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchemas();
  }, [fetchSchemas]);

  const loadPending = useCallback(async () => {
    setPendingError(null);
    try {
      const rows = await ListScheduledFridgeSchemaChanges();
      const map = {};
      rows.forEach((r) => {
        if (r.status && r.status !== 'PENDING') return;
        if (!map[r.schemaId]) map[r.schemaId] = [];
        map[r.schemaId].push(r);
      });
      Object.values(map).forEach((arr) =>
        arr.sort((a, b) =>
          (a.actionTime || '').localeCompare(b.actionTime || ''),
        ),
      );
      setPendingBySchema(map);
      return map;
    } catch (err) {
      console.error('Failed to load scheduled-change indicators:', err);
      setPendingError(
        err?.errors?.[0]?.message ||
          err?.message ||
          'Failed to load scheduled-change indicators.',
      );
      return null;
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const handleCancelScheduled = async (jobId, schemaId) => {
    const key = `${jobId}-${schemaId}`;
    setCancellingKey(key);
    setCancelError(null);
    try {
      await CancelScheduledFridgeSchemaChange(jobId, schemaId);
      const map = await loadPending();
      if (map && !(map[schemaId] || []).length) setModalSchemaId(null);
    } catch (err) {
      setCancelError(
        err?.errors?.[0]?.message ||
          err?.message ||
          'Failed to cancel this scheduled change.',
      );
    } finally {
      setCancellingKey(null);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);

    if (value === '' || value === 'All Schemas') {
      setSelectedSchema('ALL');
    } else {
      setSelectedSchema(value);
    }
  };

  const handleSchemaSelect = (schema) => {
    setSearchTerm(schema === 'ALL' ? 'All Schemas' : schema);
    setSelectedSchema(schema);
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => setShowDropdown(false), 200);
  };

  // Filter schemas based on search term
  const filteredSchemas = schemas.filter((schema) =>
    schema.toLowerCase().includes(debouncedSearchTerm.toLowerCase()),
  );

  const handleViewConditions = () => {
    setLoading(true);
    setError(null);
    try {
      const filteredConditions = getFilteredConditions();
      if (!filteredConditions.length) {
        setError(noResultsMessage());
        setShowConditions(false);
        setSchemaConditions([]);
        return;
      }
      setSchemaConditions(filteredConditions);
      setFilters(emptyFilters());
      setShowConditions(true);
    } catch (err) {
      setError('Failed to display schema conditions.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    setLoading(true);
    setError(null);
    try {
      const filteredConditions = showConditions
        ? displayedConditions
        : getFilteredConditions();
      if (!filteredConditions.length) {
        setError(noResultsMessage());
        setLoading(false);
        return;
      }

      // Helper function to escape CSV fields
      const escapeCsvField = (field) => {
        if (field === null || field === undefined || field === '') return '';
        const stringField = String(field);
        // If field contains comma, newline, or quotes, wrap in quotes and escape existing quotes
        if (
          stringField.includes(',') ||
          stringField.includes('\n') ||
          stringField.includes('"')
        ) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      // Helper function to format timeBoundSchedule for CSV
      const formatTimeBoundScheduleForCsv = (schedule) => {
        if (!schedule) return '';
        try {
          const parsed = JSON.parse(schedule);
          return Object.entries(parsed)
            .map(
              ([day, times]) =>
                `${day.toLowerCase()} ${times
                  .map((t) => t.join('-'))
                  .join(', ')}`,
            )
            .join('; ');
        } catch {
          return schedule;
        }
      };

      // Helper function to remove % from discount amount
      const cleanDiscountAmount = (discount) => {
        if (!discount) return '';
        return String(discount).replace('%', '');
      };

      // Create CSV content - column order matches upload format
      const csvHeaders = [
        'schemaId',
        'schemaType',
        'discount',
        'sharedSubsidyClientDiscount',
        'discountSchedule',
        'discountUI',
        'expiringDays',
        'priceDisplayType',
        'platform',
        'sleepScreenId',
      ];

      const csvContent = [
        csvHeaders.join(','),
        ...filteredConditions.map((row) =>
          [
            escapeCsvField(row.schemaId),
            escapeCsvField(row.schemaType),
            escapeCsvField(cleanDiscountAmount(row.discountAmount)),
            escapeCsvField(
              cleanDiscountAmount(row.sharedSubsidyClientDiscount),
            ),
            escapeCsvField(
              formatTimeBoundScheduleForCsv(row.timeBoundSchedule),
            ),
            row.discountUI ? 'TRUE' : '',
            escapeCsvField(row.expiringDays),
            escapeCsvField(row.priceDisplayType),
            escapeCsvField(row.platform),
            escapeCsvField(row.sleepScreenId),
          ].join(','),
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'schema_conditions.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download schema conditions.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFullSnapshot = () => {
    setLoading(true);
    setError(null);
    try {
      const filteredConditions = showConditions
        ? displayedConditions
        : getFilteredConditions();
      if (!filteredConditions.length) {
        setError(noResultsMessage());
        setLoading(false);
        return;
      }

      const escapeCsvField = (field) => {
        if (field === null || field === undefined || field === '') return '';
        const stringField = String(field);
        if (
          stringField.includes(',') ||
          stringField.includes('\n') ||
          stringField.includes('"')
        ) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      const cleanDiscountAmount = (discount) => {
        if (!discount) return '';
        return String(discount).replace('%', '');
      };

      const boolToCsv = (v) => {
        if (v === true) return 'TRUE';
        if (v === false) return 'FALSE';
        return '';
      };

      const formatExpiringLayersForCsv = (layers) => {
        if (!layers || !layers.length) return '';
        return layers.map((l) => `${l.hours}h @ ${l.discount}%`).join('\n');
      };

      const csvHeaders = [
        'schemaId',
        'pricingType',
        'schemaType',
        'discountUI',
        'priceDisplayType',
        'platform',
        'expiringDays',
        'discountAmount',
        'expiringLayers',
        'timeBoundSchedule',
        'sharedSubsidyClientDiscount',
        'sleepScreenId',
        'newFridgeExperienceSleepScreenId',
        'newFridgeExperienceEnabled',
      ];

      const csvContent = [
        csvHeaders.join(','),
        ...filteredConditions.map((row) =>
          [
            escapeCsvField(row.schemaId),
            escapeCsvField(row.pricingType),
            escapeCsvField(row.schemaType),
            boolToCsv(row.discountUI),
            escapeCsvField(row.priceDisplayType),
            escapeCsvField(row.platform),
            escapeCsvField(row.expiringDays),
            escapeCsvField(cleanDiscountAmount(row.discountAmount)),
            escapeCsvField(formatExpiringLayersForCsv(row.expiringLayers)),
            escapeCsvField(row.timeBoundSchedule || ''),
            escapeCsvField(
              cleanDiscountAmount(row.sharedSubsidyClientDiscount),
            ),
            escapeCsvField(row.sleepScreenId),
            escapeCsvField(row.newFridgeExperienceSleepScreenId),
            boolToCsv(row.newFridgeExperienceEnabled),
          ].join(','),
        ),
      ].join('\n');

      const today = new Date().toISOString().slice(0, 10);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `schema_conditions_snapshot_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download full snapshot.');
    } finally {
      setLoading(false);
    }
  };

  // Format discount amount for table display (adds % symbol)
  const formatDiscountForDisplay = (discount) => {
    if (!discount) return '-';
    return `${discount}%`;
  };

  const formatExpiringLayersDetails = (layers) => {
    if (!layers || layers.length === 0) return '-';
    return (
      <div className={styles.scheduleLines}>
        {layers.map((l, idx) => (
          <div key={idx}>{`${l.hours}h @ ${l.discount}%`}</div>
        ))}
      </div>
    );
  };

  const formatTimeBoundSchedule = (schedule) => {
    if (!schedule) return '-';
    try {
      const parsed = JSON.parse(schedule);
      const days = Object.entries(parsed).map(
        ([day, times]) => `${day}: ${times.map((t) => t.join('-')).join(', ')}`,
      );

      return (
        <div className={styles.scheduleLines}>
          {days.map((daySchedule, idx) => (
            <div key={idx}>{daySchedule}</div>
          ))}
        </div>
      );
    } catch {
      return schedule;
    }
  };

  // Format schedule for clipboard (plain text)
  const formatTimeBoundScheduleForClipboard = (schedule) => {
    if (!schedule) return '';
    try {
      const parsed = JSON.parse(schedule);
      return Object.entries(parsed)
        .map(
          ([day, times]) =>
            `${day.toLowerCase()} ${times.map((t) => t.join('-')).join(', ')}`,
        )
        .join('; ');
    } catch {
      return schedule;
    }
  };

  const handleCopyToClipboard = async () => {
    const headers = [
      'schemaId',
      'schemaType',
      'discount',
      'sharedSubsidyClientDiscount',
      'discountSchedule',
      'discountUI',
      'expiringDays',
      'priceDisplayType',
      'platform',
      'sleepScreenId',
    ];

    const cleanDiscountAmount = (discount) => {
      if (!discount) return '';
      return String(discount).replace('%', '');
    };

    const rows = displayedConditions.map((condition) => [
      condition.schemaId || '',
      condition.schemaType || '',
      cleanDiscountAmount(condition.discountAmount),
      cleanDiscountAmount(condition.sharedSubsidyClientDiscount),
      formatTimeBoundScheduleForClipboard(condition.timeBoundSchedule),
      condition.discountUI ? 'TRUE' : '',
      condition.expiringDays || '',
      condition.priceDisplayType || '',
      condition.platform || '',
      condition.sleepScreenId || '',
    ]);

    const ok = await copyToClipboard(buildTsv(headers, rows));
    if (!ok) {
      setError('Failed to copy to clipboard');
      return;
    }
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <main className={styles.container}>
      <h1>View Fridge Schema Conditions</h1>
      <nav className={styles.crossLinks} aria-label="Related pages">
        <Link to="/fridge-schemas/upload" className={styles.crossLink}>
          Manage Fridge Schema Conditions →
        </Link>
      </nav>
      <TipsPanel tips={SCHEMA_CONDITIONS_TIPS} />
      <section className={styles.card}>
        <h2>View or download Fridge Schema Conditions</h2>
        <div className={styles.formRow}>
          <label htmlFor="schemaSearch">Filter by Schema:</label>
          <div className={styles.searchContainer}>
            <input
              id="schemaSearch"
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="Search schema or Select All Schemas"
              className={styles.searchInput}
              disabled={loading}
            />
            {showDropdown && (
              <div className={styles.dropdown}>
                <div
                  className={`${styles.dropdownItem} ${
                    selectedSchema === 'ALL' ? styles.selected : ''
                  }`}
                  onClick={() => handleSchemaSelect('ALL')}
                >
                  All Schemas
                </div>
                {filteredSchemas.map((schema) => (
                  <div
                    key={schema}
                    className={`${styles.dropdownItem} ${
                      selectedSchema === schema ? styles.selected : ''
                    }`}
                    onClick={() => handleSchemaSelect(schema)}
                  >
                    {schema}
                  </div>
                ))}
                {filteredSchemas.length === 0 &&
                  searchTerm &&
                  searchTerm !== 'All Schemas' && (
                    <div className={styles.noResults}>No schemas found</div>
                  )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.formRow}>
          <label htmlFor="filterList">Or paste schema IDs:</label>
          <div className={styles.filterListContainer}>
            <textarea
              id="filterList"
              value={filterListInput}
              onChange={(e) => setFilterListInput(e.target.value)}
              placeholder="One per line, or comma-separated. Overrides the dropdown when populated."
              className={styles.filterListTextarea}
              disabled={loading}
              rows={3}
            />
            {filterCounts && (
              <div className={styles.filterCounts}>
                <span>{filterCounts.matched} matched</span>
                {filterCounts.notFound > 0 && (
                  <span className={styles.filterCountsNotFound}>
                    , {filterCounts.notFound} not found
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setFilterListInput('')}
                  className={styles.filterClearButton}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>
        <div className={styles.buttonRow}>
          <button
            className={styles.button}
            onClick={handleViewConditions}
            disabled={loading || schemas.length === 0}
          >
            {loading ? 'Loading...' : 'View Schema Conditions'}
          </button>
          <button
            className={styles.button}
            onClick={handleDownload}
            disabled={loading || schemas.length === 0}
            title="Download a CSV of fields you can edit and re-upload to update fridges in bulk. Excludes read-only display fields."
          >
            {loading ? 'Loading...' : 'Download for Editing'}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleDownloadFullSnapshot}
            disabled={loading || schemas.length === 0}
            title="Download a CSV of every column on this page, including read-only fields. For analysis only — uploading this CSV will only update the editable columns."
          >
            {loading ? 'Loading...' : 'Download Full Snapshot'}
          </button>
        </div>
        {error && <div className={styles.errorMsg}>{error}</div>}
        {pendingError && (
          <div className={styles.errorMsg}>
            Scheduled-change indicators didn’t load: {pendingError}
          </div>
        )}
      </section>

      {showConditions && schemaConditions.length > 0 && (
        <section className={styles.conditionsCard}>
          <div className={styles.tableHeader}>
            <h2>Schema Conditions</h2>
            <button
              className={styles.copyButton}
              onClick={handleCopyToClipboard}
              title="Copy table data to clipboard for pasting into Google Sheets"
            >
              {copySuccess ? '✓ Copied!' : '📋 Copy to Clipboard'}
            </button>
          </div>
          {displayedConditions.some((c) => isBaseSchema(c.schemaId)) && (
            <p className={styles.baseLegend}>
              <span className={styles.baseMarker}>*</span>
              Base/master schema — new fridges are copied from these. Avoid
              editing conditions here; changes flow to every future copy.
            </p>
          )}
          <div className={styles.filterBar}>
            {facets.types.length > 0 && (
              <div className={styles.filterGroup}>
                <span className={styles.filterGroupLabel}>Type</span>
                {facets.types.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`${styles.filterPill} ${
                      filters.types.has(t) ? styles.filterPillActive : ''
                    }`}
                    onClick={() => toggleSetFilter('types', t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
            {facets.platforms.length > 0 && (
              <div className={styles.filterGroup}>
                <span className={styles.filterGroupLabel}>Platform</span>
                {facets.platforms.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`${styles.filterPill} ${
                      filters.platforms.has(p) ? styles.filterPillActive : ''
                    }`}
                    onClick={() => toggleSetFilter('platforms', p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            {facets.priceTypes.length > 0 && (
              <div className={styles.filterGroup}>
                <span className={styles.filterGroupLabel}>Price Display</span>
                {facets.priceTypes.map((pt) => (
                  <button
                    key={pt}
                    type="button"
                    className={`${styles.filterPill} ${
                      filters.priceTypes.has(pt) ? styles.filterPillActive : ''
                    }`}
                    onClick={() => toggleSetFilter('priceTypes', pt)}
                  >
                    {pt}
                  </button>
                ))}
              </div>
            )}
            {(facets.hasBase || facets.hasNfe || facets.hasScheduled) && (
              <div className={styles.filterGroup}>
                <span className={styles.filterGroupLabel}>Flags</span>
                {facets.hasBase && (
                  <button
                    type="button"
                    className={`${styles.filterPill} ${
                      filters.base ? styles.filterPillActive : ''
                    }`}
                    onClick={() => toggleBoolFilter('base')}
                  >
                    Base
                  </button>
                )}
                {facets.hasNfe && (
                  <button
                    type="button"
                    className={`${styles.filterPill} ${
                      filters.nfe ? styles.filterPillActive : ''
                    }`}
                    onClick={() => toggleBoolFilter('nfe')}
                  >
                    NFE
                  </button>
                )}
                {facets.hasScheduled && (
                  <button
                    type="button"
                    className={`${styles.filterPill} ${
                      styles.filterPillScheduled
                    } ${filters.scheduled ? styles.filterPillActive : ''}`}
                    onClick={() => toggleBoolFilter('scheduled')}
                  >
                    <ClockIcon size={13} />
                    {' Scheduled'}
                  </button>
                )}
              </div>
            )}
          </div>
          {anyFilterActive && (
            <div className={styles.filterMeta}>
              <span>
                Showing {displayedConditions.length} of{' '}
                {schemaConditions.length}
              </span>
              <button
                type="button"
                className={styles.filterMetaClear}
                onClick={clearFilters}
              >
                Clear filters
              </button>
            </div>
          )}
          <div className={styles.conditionsTable}>
            <table>
              <thead>
                <tr>
                  <th>Schema ID</th>
                  <th>Schema Type</th>
                  <th>Discount</th>
                  <th>Client Subsidy</th>
                  <th>Discount Schedule</th>
                  <th>Discount UI</th>
                  <th>Price Display Type</th>
                  <th>Platform</th>
                  <th>Expiring Details</th>
                  <th>Standard Sleep Screen</th>
                  <th>NFE</th>
                  <th>NFE Active</th>
                </tr>
              </thead>
              <tbody>
                {displayedConditions.length === 0 && (
                  <tr>
                    <td colSpan={12} className={styles.noResults}>
                      No schemas match the active filters.
                    </td>
                  </tr>
                )}
                {displayedConditions.map((condition, index) => (
                  <tr key={index}>
                    <td>
                      <span className={styles.schemaIdCell}>
                        <span className={styles.schemaIdText}>
                          {condition.schemaId || '-'}
                          {isBaseSchema(condition.schemaId) && (
                            <span
                              className={styles.baseMarker}
                              title="Base/master schema — new fridges are copied from this. Avoid editing; changes flow to every future copy."
                            >
                              *
                            </span>
                          )}
                        </span>
                        {(pendingBySchema[condition.schemaId] || []).length >
                          0 && (
                          <button
                            type="button"
                            className={styles.scheduledClock}
                            onClick={() => {
                              setCancelError(null);
                              setModalSchemaId(condition.schemaId);
                            }}
                            title={`Change scheduled for ${formatScheduleDateTime(
                              pendingBySchema[condition.schemaId][0].actionTime,
                            )} — click to view or cancel`}
                            aria-label={`Change scheduled for ${formatScheduleDateTime(
                              pendingBySchema[condition.schemaId][0].actionTime,
                            )} — view or cancel`}
                          >
                            <ClockIcon />
                          </button>
                        )}
                      </span>
                    </td>
                    <td>{condition.schemaType || '-'}</td>
                    <td>
                      {formatDiscountForDisplay(condition.discountAmount)}
                    </td>
                    <td>
                      {formatDiscountForDisplay(
                        condition.sharedSubsidyClientDiscount,
                      )}
                    </td>
                    <td className={styles.scheduleCell}>
                      {formatTimeBoundSchedule(condition.timeBoundSchedule)}
                    </td>
                    <td>{condition.discountUI ? 'TRUE' : '-'}</td>
                    <td>{condition.priceDisplayType || '-'}</td>
                    <td>{condition.platform || '-'}</td>
                    <td>
                      {formatExpiringLayersDetails(condition.expiringLayers)}
                    </td>
                    <td>
                      {condition.newFridgeExperienceEnabled
                        ? '-'
                        : condition.sleepScreenId || '-'}
                    </td>
                    <td>{condition.newFridgeExperienceSleepScreenId || '-'}</td>
                    <td>
                      {condition.newFridgeExperienceEnabled ? (
                        <Badge variant="warning">NFE</Badge>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {modalSchemaId && (
        <ScheduledChangeModal
          schemaId={modalSchemaId}
          rows={pendingBySchema[modalSchemaId] || []}
          onClose={() => setModalSchemaId(null)}
          onCancelRow={handleCancelScheduled}
          cancellingKey={cancellingKey}
          cancelError={cancelError}
        />
      )}
    </main>
  );
};

export default SchemaConditionsLanding;
